"use client";

import { t } from "@lingui/core/macro";
import { create } from "zustand";
import type { Message } from "@/components/core/alert";

// Define the store type
interface AlertStore {
  message: Message | null;
  setMessage: (message: Message | null) => void;
  showUnexpectedErrorMessage: () => void;
  showTooManyAttemptsMessage: () => void;
  showErrorMessage: (text: Message["text"]) => void;
  showSuccessMessage: (text: Message["text"]) => void;
  showError: (text?: Message["text"]) => void;
  showSuccess: (text: Message["text"]) => void;
  showInfo: (text: Message["text"]) => void;
  showWarning: (text: Message["text"]) => void;
  clearMessage: () => void;
}

// Create the Zustand store
export const useAlertStore = create<AlertStore>((set) => ({
  message: null,
  setMessage: (message) => {
    if (!message) {
      set({ message: null });
      return;
    }
    set({ message });
  },
  showUnexpectedErrorMessage: () =>
    set({
      message: {
        text: t`An unexpected error occurred. Please try again later.`,
        severity: "error",
      },
    }),
  showTooManyAttemptsMessage: () =>
    set({
      message: {
        text: t`Too many attempts. Please try again later.`,
        severity: "error",
      },
    }),
  showErrorMessage: (text) =>
    set({
      message: {
        text,
        severity: "error",
      },
    }),
  showSuccessMessage: (text) =>
    set({
      message: {
        text,
        severity: "success",
      },
    }),
  showError: (text) =>
    set({
      message: {
        text: text ?? t`An unexpected error occurred. Please try again later.`,
        severity: "error",
      },
    }),
  showSuccess: (text) =>
    set({
      message: {
        text,
        severity: "success",
      },
    }),
  showInfo: (text) =>
    set({
      message: {
        text,
        severity: "info",
      },
    }),
  showWarning: (text) =>
    set({
      message: {
        text,
        severity: "warning",
      },
    }),
  clearMessage: () => set({ message: null }),
}));
